import { type JSX, useState } from "react";
import { OfferEditableWrap } from "@/components/ClientOfferView";
import styles from "@/components/ClientOfferView.module.css";
import { buildOfferNumber } from "@/components/offer/offerDocumentUtils";
import type { OfferSectionProps } from "@/components/offer/offerDocumentTypes";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export function CTASection({ offerData }: OfferSectionProps): JSX.Element {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const {
    offerTexts,
    onOfferTextsChange,
    companyDataLoading = false,
    companyName,
    clientName,
    companyEmail,
  } = offerData;

  const brandName = companyName?.trim() || "Wykonawca";
  const clientNameTrimmed = clientName?.trim() || "-";
  const closingLeadText =
    offerTexts?.closingLead?.trim() || "Dziękujemy za zaproszenie do wyceny.";
  const closingSecondText = offerTexts?.closingSecond?.trim() || "";
  const baseMail = companyEmail?.trim() || "";
  const showActions = offerData.templateType !== "quick";
  const hasContractorEmail = baseMail.length > 0;
  const offerNumber = offerData.offerNumber?.trim() || buildOfferNumber(offerData.draftId, offerData.offerDate);
  const isPublicOffer = offerData.isPublicOfferView === true && Boolean(offerData.publicOfferToken);
  const wasAccepted = Boolean(offerData.publicOfferAcceptedAt);

  const buildMailto = (subject: string): string =>
    `mailto:${baseMail}?subject=${encodeURIComponent(subject)}`;

  const handleAcceptClick = () => {
    setConfirmOpen(true);
  };

  const handleAcceptConfirm = async () => {
    if (!offerData.onPublicOfferAccept) return;
    setConfirmOpen(false);
    await offerData.onPublicOfferAccept(clientName?.trim() || "");
  };

  return (
    <section className={styles.closing}>
      {onOfferTextsChange ? (
        <OfferEditableWrap enabled>
          <p
            className={styles.pullQuote}
            contentEditable
            suppressContentEditableWarning
            onBlur={(e) =>
              onOfferTextsChange({
                closingLead: e.currentTarget.textContent?.trim() ?? "",
              })
            }
          >
            {closingLeadText}
          </p>
        </OfferEditableWrap>
      ) : (
        <p className={styles.pullQuote}>{closingLeadText}</p>
      )}
      {closingSecondText ? <p className={styles.closingBody}>{closingSecondText}</p> : null}
      <div className={styles.signatures}>
        <div className={styles.sig}>
          <div className={styles.sigLabel}>Wykonawca</div>
          <div className={styles.sigWho}>{companyDataLoading ? "Ładowanie danych firmy..." : brandName}</div>
        </div>
        <div className={styles.sig}>
          <div className={styles.sigLabel}>Klient - akceptacja warunków</div>
          <div className={styles.sigWho}>{clientNameTrimmed}</div>
        </div>
      </div>
      {isPublicOffer && wasAccepted ? (
        <div className={styles.acceptedNotice}>
          ✓ Oferta zaakceptowana. Wykonawca zostanie powiadomiony.
        </div>
      ) : showActions || isPublicOffer ? (
        <div className={`${styles.actions} no-print`}>
          {isPublicOffer ? (
            <button
              type="button"
              className={`${styles.btn} ${styles.btnPrimary}`}
              onClick={handleAcceptClick}
              disabled={offerData.publicOfferAccepting}
            >
              {offerData.publicOfferAccepting ? "Zapisywanie..." : "Akceptuję ofertę"}
            </button>
          ) : (
            <a className={`${styles.btn} ${styles.btnPrimary}`} href={buildMailto("Akceptacja oferty")}>
              Akceptuję ofertę
            </a>
          )}

          {hasContractorEmail ? (
            <a className={styles.btn} href={buildMailto(`Pytanie do oferty [${offerNumber}]`)}>
              Mam pytanie
            </a>
          ) : null}
          {hasContractorEmail ? (
            <a className={styles.btn} href={buildMailto(`Prośba o zmianę w ofercie [${offerNumber}]`)}>
              Proszę o zmianę
            </a>
          ) : null}
        </div>
      ) : null}

      {isPublicOffer && offerData.publicOfferAcceptError ? (
        <p className="mt-2 text-sm text-destructive">{offerData.publicOfferAcceptError}</p>
      ) : null}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Czy potwierdzasz akceptację oferty?</AlertDialogTitle>
            <AlertDialogDescription>Po akceptacji wykonawca otrzyma potwierdzenie.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Anuluj</AlertDialogCancel>
            <AlertDialogAction onClick={() => void handleAcceptConfirm()}>
              Tak, akceptuję
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
